/* Gift wrapping in cart — the in-cart banner writes line-item properties + one marker
   wrapping line; a Shopify Cart Transform function prices it server-side. Banner DOM lives
   in sections/mini-cart.liquid. Gated by the `gw-active` body class (the theme adds it when
   the "Gift wrapping active" theme setting is on); when off, nothing here runs. */
(() => {
  'use strict';

  // Feature gate — theme adds `gw-active` to <body> when the setting is on.
  const ACTIVE = () => document.body.classList.contains('gw-active');

  const PROP = '_gw_count';                // wrapped-unit count, per product line (hidden from cart/checkout)
  const MARKER = '_gift_wrapping_total';   // hidden marker the Cart Transform function reads

  const cfg = () => {
    try { return JSON.parse(document.getElementById('gw-config').textContent); }
    catch { return {}; }
  };
  // Format cents using the shop's money format (from gw-config), not a hardcoded locale.
  const money = (cents) => {
    const fmt = cfg().moneyFormat || '{{amount}}';
    const num = (p, t, d) => {
      const s = (cents / 100).toFixed(p).split('.');
      return s[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + t) + (s[1] ? d + s[1] : '');
    };
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, k) =>
      k === 'amount_no_decimals' ? num(0, ',', '.')
      : k === 'amount_with_comma_separator' ? num(2, '.', ',')
      : k === 'amount_no_decimals_with_comma_separator' ? num(0, '.', ',')
      : k === 'amount_with_apostrophe_separator' ? num(2, "'", '.')
      : num(2, ',', '.'));
  };
  const modal = () => document.getElementById('gw-modal');

  /* ===== In-cart banner + bottom-sheet selector ===== */
  // Delegated so they survive drawer re-renders
  document.addEventListener('click', (e) => {
    if (!ACTIVE()) return;
    if (e.target.closest('[data-gw-open]')) { e.preventDefault(); return openSheet(); }
    if (e.target.closest('[data-gw-close]')) { e.preventDefault(); return closeSheet(); }
    if (e.target.closest('[data-gw-confirm]')) { e.preventDefault(); return confirmSelection(); }
  });
  document.addEventListener('change', (e) => {
    if (!ACTIVE()) return;
    if (e.target.classList && e.target.classList.contains('gw-card__check')) updateSummary();
  });

  function openSheet() {
    const m = modal();
    if (!m) return;
    m.hidden = false;
    requestAnimationFrame(() => m.classList.add('gw-modal--open'));
    updateSummary();
  }
  function closeSheet() {
    const m = modal();
    if (!m) return;
    m.classList.remove('gw-modal--open');
    setTimeout(() => { m.hidden = true; }, 280);
  }
  function checkedBoxes() {
    return [...document.querySelectorAll('#gw-modal .gw-card__check:checked:not(:disabled)')];
  }
  function priceFor(n, c) {
    return n > 0 ? c.basePrice + Math.max(0, n - 1) * c.addonPrice : 0;
  }
  function updateSummary() {
    const m = modal();
    if (!m) return;
    const c = cfg();
    const n = checkedBoxes().length;
    const hint = m.querySelector('.gw-modal__hint');
    if (hint) hint.hidden = n < 1;
    const summary = m.querySelector('.gw-modal__summary');
    if (summary) {
      summary.innerHTML = n
        ? `${(c.summaryText || '').replace(/\{total\}/g, n)} · <span class="gw-modal__summary-price">Gesamtpreis: ${money(priceFor(n, c))}</span>`
        : '';
    }
    const btn = m.querySelector('.gw-modal__confirm');
    if (btn) btn.classList.toggle('gw-modal__confirm--filled', n > 0);
  }

  // Locale-aware cart routes — hardcoded paths dropped the connection on this store
  const ROUTES = (window.themeVariables && window.themeVariables.routes) || {};
  const CART_BASE = ROUTES.cartUrl || '/cart';
  const URL_CART = CART_BASE + '.js';
  const URL_CHANGE = (ROUTES.cartChangeUrl || '/cart/change') + '.js';
  const URL_ADD = CART_BASE + '/add.js';
  const URL_UPDATE = CART_BASE + '/update.js';

  async function postJSON(url, body) {
    const r = await fetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    return r.json();
  }
  async function getJSON(url) {
    const r = await fetch(url, { credentials: 'same-origin' });
    return r.json();
  }

  // No-op cart update that returns the re-rendered drawer section
  function fetchSection() {
    return postJSON(URL_UPDATE, {
      updates: {},
      sections: ['mini-cart'],
      sections_url: window.location.pathname
    });
  }
  function refreshDrawer(res) {
    const detail = { cart: res, replacementDelay: 0 };
    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
  }

  async function confirmSelection() {
    const c = cfg();
    const btn = document.querySelector('#gw-modal .gw-modal__confirm');
    if (btn) btn.disabled = true;

    try {
      const counts = {};
      checkedBoxes().forEach((b) => { counts[b.dataset.key] = (counts[b.dataset.key] || 0) + 1; });
      const totalUnits = Object.values(counts).reduce((s, x) => s + x, 0);

      const cart = await getJSON(URL_CART);

      // Tag each eligible line with its wrapped count (preserving other props)
      for (const li of cart.items) {
        if (li.handle === c.wrapHandle) continue;
        const desired = counts[li.key] || 0;
        const current = parseInt((li.properties && li.properties[PROP]) || '0', 10);
        if (desired === current) continue;

        const props = Object.assign({}, li.properties || {});
        props[PROP] = desired > 0 ? String(desired) : ''; // '' not delete — change.js ignores an empty {}
        await postJSON(URL_CHANGE, { id: li.key, quantity: li.quantity, properties: props });
      }

      // Keep exactly one wrapping line; the Cart Transform function prices it. Pass the
      // base + add-on (cents, from settings) as hidden props so the function stays dynamic.
      const wrapLine = cart.items.find((li) => li.handle === c.wrapHandle);
      if (totalUnits > 0 && !wrapLine && c.variantId) {
        await postJSON(URL_ADD, { id: c.variantId, quantity: 1, properties: { [MARKER]: '1', _gw_base: String(c.basePrice), _gw_addon: String(c.addonPrice) } });
      } else if (totalUnits < 1 && wrapLine) {
        await postJSON(URL_CHANGE, { id: wrapLine.key, quantity: 0 });
      }

      const res = await fetchSection();
      closeSheet();
      refreshDrawer(res);
    } catch (err) {
      console.error('[gift-wrapping] confirm failed', err);
    } finally {
      if (btn) btn.disabled = false;
    }
  }

  /* ===== Hide the gift-wrapping offer inside the Candy Rack popup =====
     The popup still shows its other upsells; only our gift product is hidden (wrapping is
     offered via the in-cart banner). A <style> injected into the same-origin frame persists
     across the app's React re-renders and never flashes. */
  const CR_FRAME = 'candyrack-frame';
  const giftProductId = () => String(cfg().productId || '');

  function frameDoc() {
    const f = document.getElementById(CR_FRAME);
    try { return f && f.contentDocument ? f.contentDocument : null; } catch { return null; }
  }
  function hideGiftOffer(doc) {
    if (!ACTIVE() || !doc) return;
    const pid = giftProductId();
    if (!pid || doc.getElementById('gw-hide-offer')) return;
    const style = doc.createElement('style');
    style.id = 'gw-hide-offer';
    style.textContent = `li[class*="Product-Id-${pid}"]{display:none !important;}`;
    (doc.head || doc.documentElement).appendChild(style);
  }
  const isOffersUrl = (u) => /candyrack/i.test(u || '') && /\/offers\b/i.test(u || '');
  // Re-assert on each popup open (Candy Rack may rebuild the frame doc)
  function onOffersOpen() {
    if (!ACTIVE()) return;
    const apply = () => { bindFrame(); const d = frameDoc(); if (d) hideGiftOffer(d); };
    apply();
    setTimeout(apply, 150);
    setTimeout(apply, 600);
  }

  /* ===== Keep the cart tidy: prune the wrapping line when nothing is wrapped ===== */
  function wrappedUnits(cart) {
    const c = cfg();
    let n = 0;
    for (const li of cart.items) {
      if (li.handle === c.wrapHandle) continue;
      n += parseInt((li.properties && li.properties[PROP]) || '0', 10) || 0;
    }
    return n;
  }
  let pruning = false;
  let pruneTimer = null;
  const schedulePrune = () => { clearTimeout(pruneTimer); pruneTimer = setTimeout(pruneOrphanWrapping, 200); };
  async function pruneOrphanWrapping() {
    if (pruning) return;
    const c = cfg();
    let cart;
    try { cart = await getJSON(URL_CART); } catch { return; }
    const giftLines = cart.items.filter((x) => x.handle === c.wrapHandle);
    if (!giftLines.length || wrappedUnits(cart) > 0) return; // still wrapping something — keep it
    pruning = true;
    try {
      for (const g of giftLines) await postJSON(URL_CHANGE, { id: g.key, quantity: 0 });
      const res = await fetchSection();
      refreshDrawer(res);
    } catch (err) { console.error('[gift-wrapping] prune failed', err); }
    finally { pruning = false; }
  }

  const onCartMutation = () => { if (ACTIVE()) schedulePrune(); };

  /* ===== Watchers: cart mutations (prune) + Candy Rack offers-open (hide) ===== */
  const _fetch = window.fetch;
  window.fetch = function (input) {
    const url = (typeof input === 'string' ? input : (input && input.url)) || '';
    if (isOffersUrl(url)) onOffersOpen();
    const out = _fetch.apply(this, arguments);
    if (/\/cart\/(add|change|update)/.test(url)) out.then(onCartMutation).catch(() => {});
    return out;
  };
  const _open = XMLHttpRequest.prototype.open;
  const _send = XMLHttpRequest.prototype.send;
  XMLHttpRequest.prototype.open = function () { this.__gwUrl = arguments[1] || ''; if (isOffersUrl(this.__gwUrl)) onOffersOpen(); return _open.apply(this, arguments); };
  XMLHttpRequest.prototype.send = function () {
    if (/\/cart\/(add|change|update)/.test(this.__gwUrl || '')) this.addEventListener('loadend', onCartMutation);
    return _send.apply(this, arguments);
  };
  document.documentElement.addEventListener('cart:updated', onCartMutation);

  /* ===== Bind into the Candy Rack frame to keep the gift offer hidden ===== */
  function bindFrame() {
    const doc = frameDoc();
    if (!doc || doc.__gwBound) return;
    if (!ACTIVE()) return;
    doc.__gwBound = true;

    // Re-inject the hide-style on every render (idempotent).
    new MutationObserver(() => hideGiftOffer(doc))
      .observe(doc.documentElement || doc, { childList: true, subtree: true, characterData: true });

    // The offers request may originate inside the frame — patch its fetch/XHR too.
    const win = doc.defaultView;
    if (win && !win.__gwOffersPatched) {
      win.__gwOffersPatched = true;
      if (win.fetch) {
        const wf = win.fetch;
        win.fetch = function (input) {
          const u = (typeof input === 'string' ? input : (input && input.url)) || '';
          if (isOffersUrl(u)) onOffersOpen();
          return wf.apply(this, arguments);
        };
      }
      if (win.XMLHttpRequest) {
        const wo = win.XMLHttpRequest.prototype.open;
        win.XMLHttpRequest.prototype.open = function () {
          if (isOffersUrl(arguments[1])) onOffersOpen();
          return wo.apply(this, arguments);
        };
      }
    }

    hideGiftOffer(doc);
  }

  // The frame is created/recreated dynamically; watch the top page and (re)bind
  new MutationObserver(bindFrame).observe(document.documentElement, { childList: true, subtree: true });
  bindFrame();

  // Safety-net poll for the FIRST open: Candy Rack builds the frame doc without a
  // top-DOM mutation, so the observer misses it. Poll until the real doc is bound.
  const bindPoll = setInterval(() => {
    if (!ACTIVE()) return;
    bindFrame();
    const d = frameDoc();
    if (d && d.__gwBound && d.querySelector('#modal-container, li[class*="Product-Id-"]')) {
      clearInterval(bindPoll);
    }
  }, 400);
})();
